import type { AlligatorLines } from "../../model/calcAlligator";
import type { AlligatorSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

export function drawAlligator(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  lines: AlligatorLines,
  settings: AlligatorSettings,
): void {
  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity, maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i];
    if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;

  const priceRange = maxPrice - minPrice || 1;
  const pricePadding = priceRange * 0.05;
  minPrice -= pricePadding;
  maxPrice += pricePadding;
  const range = maxPrice - minPrice;
  const priceToY = (price: number) => top + ((maxPrice - price) / range) * chartHeight;

  ctx.clearRect(0, 0, width, height);

  const drawLine = (values: (number | null)[], color: string, lw: number) => {
    // use values.length - 1 so shifted lines extend past the last candle into right padding
    const lineEnd = Math.min(Math.floor(end), values.length - 1);
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= lineEnd; i++) {
      const v = values[i];
      if (v === null || v === undefined) { started = false; continue; }
      const x = left + (i - startIdx + 0.5) * cw;
      const y = priceToY(v);
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };

  if (settings.jaws.visible) drawLine(lines.jaws, settings.jaws.color, settings.jaws.width);
  if (settings.teeth.visible) drawLine(lines.teeth, settings.teeth.color, settings.teeth.width);
  if (settings.lips.visible) drawLine(lines.lips, settings.lips.color, settings.lips.width);
}
